import SectionTitle from './SectionTitle'
import Entry from './Entry'
import React from 'react'

function ListHeader({children}: {children: React.ReactNode}) {
    return (
        <p className="mt-5 font-mono text-[11px] uppercase tracking-[.2em] text-accent">
            {'// '}{children}
        </p>
    )
}

export default function Experience() {
    return (
        <section>
            <SectionTitle title="Experience" index="04" />
            <div className="entry-list mt-8 space-y-6">
                {/* Betabit */}
                <Entry
                    title="Founder & Principal Engineer"
                    dates="Dec 2018 – Present"
                    company="Betabit"
                    location="San Juan, PR"
                >
                    <p>I lead Betabit, where we engineer AI, software, and cloud solutions that deliver measurable business impact—deep technical execution combined with strategic thinking.</p>

                    <ListHeader>Focus Areas</ListHeader>
                    <ul className="hud-list mt-3 space-y-2.5 text-body">
                        <li><span className="font-semibold text-ink">AI & Intelligent Systems:</span> Org-wide AI transformation, agentic development, semantic layers, MCP integrations, and intelligent automation with Claude, OpenAI, and custom tooling.</li>
                        <li><span className="font-semibold text-ink">Cloud & Infrastructure:</span> AWS architecture (Terraform/CDK), zero-downtime migrations, security hardening, and client cost reductions of $30K+/year.</li>
                        <li><span className="font-semibold text-ink">Full-Stack Development:</span> Daily production code (React, TypeScript, Python, Java)—complete, deployable software, not theoretical designs.</li>
                        <li><span className="font-semibold text-ink">Technical Leadership:</span> Fractional CTO services, team transformations, and architecture reviews. Managed teams of 20+ engineers across Paylocity, Augment Risk, Allied World, Attune Insurance, and Rimas.</li>
                    </ul>

                    <ListHeader>Key Projects</ListHeader>
                    <ul className="hud-list mt-3 space-y-2.5 text-body">
                        <li><span className="font-semibold text-ink">Augment Risk — AI Lead & Platform Architect (2025–Present)</span><br/>Led company-wide AI transformation—engineering 100% on agentic development, ~85% of all staff using AI daily—and built an AI-driven Windmill data platform that turns a natural-language request into a production pipeline and live dashboard in hours.</li>
                        <li><span className="font-semibold text-ink">Rimas — Technology Lead (2025–Present)</span><br/>Betabit is Rimas' technology arm: built their AWS cloud from scratch (fully Terraformed, GuardDuty, Slack-wired), rolled out Tailscale as official reseller, deployed AI assistants for the CEO and COO.</li>
                        <li><span className="font-semibold text-ink">Paylocity — Infrastructure Consultant</span><br/>Zero-downtime AWS migration (ECS EC2 → Fargate), cutting cloud costs 25%.</li>
                        <li><span className="font-semibold text-ink">Allied World — Data Solutions Lead</span><br/>Delivered "Harvester," a centralized ETL platform (Airflow, Windmill); reduced data processing from months to days.</li>
                        <li><span className="font-semibold text-ink">Attune Insurance — Head of DevOps</span><br/>Migrated to AWS Fargate, saving $1.2M annually; built on-demand personalized dev/test environments.</li>
                    </ul>
                </Entry>

                {/* Augment Risk — AI Lead */}
                <Entry
                    title="AI Lead & Platform Architect"
                    dates="Jan 2026 – Present"
                    company="Augment Risk"
                    location="Remote"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Own AI strategy and adoption across the company as the in-house AI lead: every employee equipped with Claude and Claude Code, ~85% of staff using AI daily for the core of their work—and climbing.</li>
                        <li>Moved engineering to 100% AI-driven delivery: specs, planning, project management, implementation, and code review all run through agentic workflows—custom skills, hooks, and enforced house practices (TDD, security, IaC) baked into the harness itself.</li>
                        <li>Built the enablement layer for non-engineers: reusable skills and tools inside the org's Claude integration, so business users automate and work with data directly—no tickets, no specs, no engineering bottleneck.</li>
                    </ul>
                </Entry>

                {/* Augment Risk — Consultant */}
                <Entry
                    title="Lead AI & Data Consultant — Contract"
                    dates="Jun 2025 – Present"
                    company="Augment Risk"
                    location="Remote"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Designed and shipped an AI-driven data platform on Windmill: describe the data product you want—sources, business logic, destination—and it's built, deployed, and live. External APIs to governed data lake to real-time dashboard—scaled, auditable, monitored, alerting—in hours instead of weeks.</li>
                        <li>Architected dual data lakes (restricted and unrestricted) with access controls, exposed to AI via MCP servers; delivered a Malloy semantic layer, in production and on track to front 100% of analytics by end of year.</li>
                        <li>Integrated Novus, NetSuite, Workday, and Rippling into the data lake, with real-time dashboards that alert and act on data as it lands.</li>
                        <li>Delivered Data Manager, an AI-assisted workflow system cutting client data onboarding from months to weeks.</li>
                        <li>Own the ecosystem end to end: Terraform IaC, Sentry observability, secure SFTP/cloud ingestion—self-healing and fully auditable.</li>
                    </ul>
                </Entry>

                {/* Rimas */}
                <Entry
                    title="Technology Lead & Fractional CTO — Contract"
                    dates="Jul 2025 – Present"
                    company="Rimas"
                    location="Remote / San Juan, PR"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Serve as Rimas' technology arm through Betabit—spearheading, building, and advising on everything technology-related.</li>
                        <li>Built their AWS cloud foundation from scratch: fully Terraformed modular infrastructure with security guardrails, GuardDuty, and Slack-wired notifications.</li>
                        <li>Rolled out Tailscale org-wide as an official Tailscale reseller—their entire network layer runs on it.</li>
                        <li>Deployed AI assistants for the CEO and COO, in constant daily use via WhatsApp—driving executive-level AI adoption directly.</li>
                        <li>Advising on data strategy: ingestion architecture, data lake vs. warehouse trade-offs, and orchestration.</li>
                    </ul>
                </Entry>

                {/* Paylocity */}
                <Entry
                    title="Cloud, Software, & Data Consultant — Contract"
                    dates="Feb 2024 – Mar 2025"
                    company="Paylocity"
                    location="Remote"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Planned and executed a zero-downtime migration of all infrastructure between two AWS accounts—modernizing mid-flight (ECS EC2 → Fargate), cutting cloud costs 25%, adding disaster recovery, and shipping logs and observability to Datadog.</li>
                        <li>Leveraged Terraform and AWS CDK to fully automate infrastructure provisioning and optimize databases.</li>
                        <li>Developed AWS Lambda application logic (TypeScript/Python)—direct software engineering beyond infrastructure.</li>
                    </ul>
                </Entry>

                {/* Allied World */}
                <Entry
                    title="AVP, Platform Lead, Data Solutions — Contract"
                    dates="Feb 2022 – Dec 2023"
                    company="Allied World"
                    location="Remote"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Architected and implemented "Harvester," a centralized data and ETL platform utilizing Airflow, Airbyte, and Windmill.</li>
                        <li>Designed and single-handedly built an internal application (React, TypeScript, Python, Windmill) that let teams across the Data org run and manage ELT jobs through a UI—no coding required.</li>
                        <li>Streamlined data-reporting processes, reducing timelines from months to mere days.</li>
                        <li>Led and mentored the data engineering team, established best practices for security, cloud operations, and development efficiency.</li>
                    </ul>
                </Entry>

                {/* Attune Insurance */}
                <Entry
                    title="Senior Engineering Manager — Contract"
                    dates="Sept 2019 – Feb 2022"
                    company="Attune Insurance"
                    location="Remote"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Most senior engineering leader in the company: reported directly to the CPO/CTO, drove quarterly planning and major technical decisions, and served as de facto CISO in security and compliance discussions.</li>
                        <li>Managed multiple cross-functional engineering teams, emphasizing performance management, career growth, mentorship, and fostering a high-performing engineering culture.</li>
                        <li>Led Engineering Managers, aligning overall management strategies and best practices company-wide.</li>
                        <li>Oversaw engineering recruitment, training, performance evaluations, and continuous improvement initiatives across teams.</li>
                    </ul>
                </Entry>

                <Entry
                    title="Head of DevSecOps — Contract"
                    dates="Dec 2018 – Feb 2022"
                    company="Attune Insurance"
                    location="Remote"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Directed migration from legacy third-party servers to AWS ECS Fargate, reducing infrastructure costs from $1.2M to ~$400K annually—containerizing complex legacy insurance systems, including Guidewire.</li>
                        <li>Implemented full-stack Infrastructure-as-Code (Terraform) for all AWS infrastructure, including IAM, networking, and security permissions.</li>
                        <li>Built "On-Demand Sandboxes," allowing employees instant creation of personalized, isolated production-like environments via a simple UI.</li>
                        <li>Owned company-wide Security Initiatives, ensuring compliance, auditing, and technical security standards.</li>
                        <li>Implemented comprehensive logging, monitoring, and alerting infrastructure for all company services, significantly improving incident detection and resolution times.</li>
                    </ul>
                </Entry>

                {/* Intent */}
                <Entry
                    title="People Manager"
                    dates="Jan 2017 – Dec 2018"
                    company="Intent Media"
                    location="New York, NY"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Managed and mentored junior engineers, overseeing career progression, training, and professional growth.</li>
                    </ul>
                </Entry>

                <Entry
                    title="DevOps & Data Engineer"
                    dates="Jan 2016 – Dec 2018"
                    company="Intent Media"
                    location="New York, NY"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Led DevOps initiatives for the Data Engineering team, streamlining deployment and infrastructure processes.</li>
                        <li>Served as Delivery Lead for Backend Team, ensuring milestones and project goals were consistently met.</li>
                        <li>Developed automated ML-model training pipelines using EMR and Spark.</li>
                        <li>Migrated legacy systems to AWS (ECS, Lambda, S3), implemented a scalable, high-traffic serverless API, and automated CI/CD processes.</li>
                    </ul>
                </Entry>

                <Entry
                    title="Full-Stack Engineer"
                    dates="Mar 2015 – Dec 2015"
                    company="Intent Media"
                    location="New York, NY"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Authored Java backend services with a strong emphasis on Test-Driven Development (TDD).</li>
                        <li>Collaborated closely across frontend and backend to ensure end-to-end feature correctness, performance, and reliability—from browser execution to server-side rendering and data flow.</li>
                        <li>Designed and built an internal lightweight JavaScript framework for rendering advertisement popups under extreme payload constraints, enabling fast load times on third-party partner sites where standard frontend frameworks were not allowed.</li>
                        <li>Conducted ongoing R&D on browser window and popup behavior across vendors, implementing adaptive techniques to maintain ad visibility despite frequent browser security and UX changes.</li>
                    </ul>
                </Entry>

                {/* Hackerati */}
                <Entry
                    title="Software Engineer"
                    dates="Jan 2015 – Sep 2015"
                    company="Hackerati"
                    location="New York, NY"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Provided consulting on Frontend development, specializing in Angular, React, and modern JavaScript frameworks for diverse clients.</li>
                    </ul>
                </Entry>

                {/* Optivon */}
                <Entry
                    title="Software Engineer"
                    dates="Oct 2013 – Dec 2014"
                    company="Optivon"
                    location="San Juan, PR"
                >
                    <ul className="hud-list space-y-2.5 text-body">
                        <li>Developed foundational components for law-enforcement software suite, including Android mobile app and AWS-based web dashboard.</li>
                        <li>Participated in full lifecycle: product ideation, architecture, frontend/backend implementation, and deployment to AWS.</li>
                    </ul>
                </Entry>
            </div>
        </section>
    )
}
